import { useEffect, useState } from "react";
import { Plus, AlertTriangle, Eye, Power, Send } from "lucide-react";
import contingenciaService from "../services/contingenciaService";
import companyContingenciaService from "../services/companyContingenciaService";
import CreateContingenciaModal from "../components/CreateContingenciaModal";
import ContingenciaDetailModal from "../components/ContingenciaDetailModal";
import Swal from "sweetalert2";

const TIPOS_CONTINGENCIA = [
  { value: 1, label: "No disponibilidad de sistema del contribuyente" },
  { value: 2, label: "No disponibilidad de sistema del Ministerio de Hacienda" },
  { value: 3, label: "Falla de energía eléctrica o internet (contribuyente)" },
  { value: 4, label: "Falla de energía eléctrica o internet (comprobada por proveedor)" },
  { value: 5, label: "Otras causas" },
];

export default function ContingenciaEventos() {
  const [eventos, setEventos] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvento, setSelectedEvento] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estado del panel de control
  const [estadoContingencia, setEstadoContingencia] = useState(null);
  const [loadingEstado, setLoadingEstado] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [tipoSeleccionado, setTipoSeleccionado] = useState(5);
  const [motivoActivacion, setMotivoActivacion] = useState("");

  const fetchEventos = async () => {
    try {
      setLoading(true);
      const res = await contingenciaService.getAll({ all: "true" });
      setEventos(Array.isArray(res) ? res : res.data || []);
    } catch (error) {
      Swal.fire("Error", "No se pudieron cargar los eventos de contingencia", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchEstado = async () => {
    try {
      setLoadingEstado(true);
      const data = await companyContingenciaService.getEstado();
      setEstadoContingencia(data);
    } catch (error) {
      console.error("Error obteniendo estado:", error);
    } finally {
      setLoadingEstado(false);
    }
  };

  useEffect(() => {
    fetchEventos();
    fetchEstado();
  }, []);

  // ✅ Activar contingencia
  const handleActivar = async () => {
    if (!motivoActivacion.trim()) {
      Swal.fire("Error", "Debes escribir un motivo antes de activar", "warning");
      return;
    }

    const confirm = await Swal.fire({
      title: "¿Activar Modo Contingencia?",
      html: `
        <p>Las facturas se firmarán localmente pero <strong>NO se enviarán a Hacienda</strong> hasta que desactives este modo.</p>
        <p style="margin-top:8px;"><strong>Motivo:</strong> ${TIPOS_CONTINGENCIA.find(t => t.value === parseInt(tipoSeleccionado))?.label}</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Sí, activar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    setProcesando(true);
    try {
      await companyContingenciaService.activar(parseInt(tipoSeleccionado), motivoActivacion.trim());
      Swal.fire("Activado", "Modo contingencia activado correctamente", "success");
      setMotivoActivacion("");
      fetchEstado();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.error || "No se pudo activar", "error");
    } finally {
      setProcesando(false);
    }
  };

  // ✅ Desactivar contingencia
  const handleDesactivar = async () => {
    const confirm = await Swal.fire({
      title: "¿Desactivar Modo Contingencia?",
      text: "Las facturas seguirán marcadas como pendientes hasta que las reenvíes manualmente.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      confirmButtonText: "Sí, desactivar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    setProcesando(true);
    try {
      const res = await companyContingenciaService.desactivar();
      Swal.fire("Desactivado", res.message, "success");
      fetchEstado();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.error || "No se pudo desactivar", "error");
    } finally {
      setProcesando(false);
    }
  };

  // ✅ Reenviar pendientes
  const handleReenviar = async () => {
    const confirm = await Swal.fire({
      title: "¿Reenviar facturas pendientes?",
      text: `Se enviarán ${estadoContingencia?.facturas_pendientes || 0} factura(s) a Hacienda.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      confirmButtonText: "Sí, reenviar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    setProcesando(true);
    try {
      const res = await companyContingenciaService.reenviarPendientes();
      const exitosas = res.resultados.filter(r => r.exito).length;
      const fallidas = res.resultados.filter(r => !r.exito).length;

      Swal.fire({
        icon: fallidas === 0 ? "success" : "warning",
        title: "Proceso completado",
        html: `<p>✅ Enviadas correctamente: ${exitosas}</p><p>❌ Con error: ${fallidas}</p>`,
      });

      fetchEstado();
      fetchEventos();
    } catch (error) {
      Swal.fire("Error", "No se pudo completar el reenvío", "error");
    } finally {
      setProcesando(false);
    }
  };

  const handleViewDetail = async (evento) => {
    try {
      const res = await contingenciaService.getById(evento.id);
      setSelectedEvento(res);
      setShowDetailModal(true);
    } catch (error) {
      Swal.fire("Error", "No se pudo cargar el detalle", "error");
    }
  };

  const activo = estadoContingencia?.modo_contingencia;

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>

      {/* PANEL DE CONTROL */}
      <div
        className="card"
        style={{
          marginBottom: "24px",
          border: activo ? "3px solid #dc2626" : "3px solid #16a34a",
        }}
      >
        <div className="card-header" style={{ background: activo ? "#fef2f2" : "#f0fdf4" }}>
          <div className="d-flex align-items-center gap-2">
            <Power size={24} color={activo ? "#dc2626" : "#16a34a"} />
            <h2 className="card-title" style={{ margin: 0 }}>
              {loadingEstado
                ? "Cargando estado..."
                : activo
                ? "🔴 Modo Contingencia ACTIVO"
                : "🟢 Sistema Normal"}
            </h2>
          </div>
        </div>

        <div className="card-body">
          {!activo ? (
            // ===== FORMULARIO PARA ACTIVAR =====
            <div>
              <div className="form-group" style={{ marginBottom: "12px" }}>
                <label className="form-label"><strong>Tipo de Contingencia</strong></label>
                <select
                  className="form-control"
                  value={tipoSeleccionado}
                  onChange={(e) => setTipoSeleccionado(e.target.value)}
                >
                  {TIPOS_CONTINGENCIA.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: "16px" }}>
                <label className="form-label"><strong>Motivo</strong></label>
                <textarea
                  className="form-control"
                  rows="2"
                  placeholder="Ej: Se cayó el internet en el establecimiento..."
                  value={motivoActivacion}
                  onChange={(e) => setMotivoActivacion(e.target.value)}
                  maxLength={500}
                />
              </div>

              <button
                className="btn"
                style={{ background: "#dc2626", color: "#fff" }}
                onClick={handleActivar}
                disabled={procesando}
              >
                <Power size={16} /> Activar Modo Contingencia
              </button>
            </div>
          ) : (
            // ===== PANEL CUANDO ESTÁ ACTIVO =====
            <div>
              <p style={{ marginBottom: "8px" }}>
                <strong>Motivo:</strong> {estadoContingencia?.contingencia_motivo || "N/A"}
              </p>
              <p style={{ marginBottom: "16px" }}>
                <strong>Activado desde:</strong>{" "}
                {estadoContingencia?.contingencia_fecha_inicio
                  ? new Date(estadoContingencia.contingencia_fecha_inicio).toLocaleString("es-SV")
                  : "N/A"}
              </p>

              <div
                style={{
                  padding: "12px 16px",
                  background: "#fef3c7",
                  border: "2px solid #f59e0b",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  fontWeight: 600,
                }}
              >
                📄 Facturas pendientes de enviar: {estadoContingencia?.facturas_pendientes ?? 0}
              </div>

              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <button
                  className="btn"
                  style={{ background: "#16a34a", color: "#fff" }}
                  onClick={handleDesactivar}
                  disabled={procesando}
                >
                  <Power size={16} /> Desactivar Modo Contingencia
                </button>
              </div>
            </div>
          )}

          {/* Botón de reenviar siempre visible si hay pendientes, activo o no */}
          {estadoContingencia?.facturas_pendientes > 0 && (
            <div style={{ marginTop: "16px", borderTop: "1px solid #e2e8f0", paddingTop: "16px" }}>
              <button
                className="btn"
                style={{ background: "#2563eb", color: "#fff" }}
                onClick={handleReenviar}
                disabled={procesando}
              >
                <Send size={16} /> Reenviar {estadoContingencia.facturas_pendientes} factura(s) pendiente(s)
              </button>
            </div>
          )}
        </div>
      </div>

      {/* TABLA DE EVENTOS YA ENVIADOS */}
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <AlertTriangle size={28} color="#7c3aed" />
            <h1 className="card-title">Eventos de Contingencia</h1>
          </div>
          <button className="btn" style={{ background: "#7c3aed", color: "#fff" }} onClick={() => setShowCreateModal(true)}>
            <Plus size={18} /> Nuevo Evento
          </button>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center" style={{ padding: "40px" }}>
              <div className="spinner" style={{ margin: "0 auto" }}></div>
              <p style={{ marginTop: "16px", color: "#718096" }}>Cargando eventos...</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Código de Generación</th>
                    <th>Período</th>
                    <th style={{ textAlign: "center" }}>Documentos</th>
                    <th style={{ textAlign: "center" }}>Estado</th>
                    <th style={{ textAlign: "center" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {eventos.length === 0 ? (
                    <tr>
                      <td colSpan="5">
                        <div className="text-center" style={{ padding: "40px 20px" }}>
                          <AlertTriangle size={48} color="#cbd5e0" style={{ marginBottom: "16px" }} />
                          <p style={{ color: "#a0aec0", fontSize: "16px" }}>No hay eventos de contingencia registrados</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    eventos.map((ev) => (
                      <tr key={ev.id}>
                        <td>
                          <strong style={{ fontSize: "11px", wordBreak: "break-all", fontFamily: "monospace" }}>
                            {ev.codigo_generacion}
                          </strong>
                        </td>
                        <td>{ev.f_inicio} {ev.h_inicio} — {ev.f_fin} {ev.h_fin}</td>
                        <td style={{ textAlign: "center" }}>
                          {Array.isArray(ev.detalle_dte) ? ev.detalle_dte.length : JSON.parse(ev.detalle_dte || "[]").length}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span style={{ padding: "4px 12px", background: ev.estado === "VALIDADO" ? "#d1fae5" : "#fee2e2", color: ev.estado === "VALIDADO" ? "#065f46" : "#991b1b", borderRadius: "6px", fontSize: "12px", fontWeight: 600 }}>
                            {ev.estado}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button className="btn btn-primary btn-sm" onClick={() => handleViewDetail(ev)} title="Ver detalle">
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateContingenciaModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); fetchEventos(); }}
        />
      )}

      {showDetailModal && selectedEvento && (
        <ContingenciaDetailModal
          evento={selectedEvento}
          onClose={() => { setShowDetailModal(false); setSelectedEvento(null); }}
        />
      )}
    </div>
  );
}